import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Camera, Plus, Trash2, Save } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import AutocompleteInput from './AutocompleteInput';
import DatePicker from '../Common/DatePicker';
import './CVEditor.scss';

interface WorkExperience {
  id: string;
  period: string;
  startDate: string;
  endDate: string;
  duration: string;
  companyName: string;
  companyWebsite: string;
  position: string;
  workNature: string;
  achievements: string;
}

interface Education {
  id: string;
  startDate: string;
  endDate: string;
  studyPeriod: string;
  institutionName: string;
  educationLevel: string;
  fieldOfStudy: string;
}

interface AdditionalTraining {
  id: string;
  year: string;
  duration: string;
  provider: string;
  courseName: string;
}

interface LanguageSkill {
  id: string;
  language: string;
  understanding: number;
  speaking: number;
  writing: number;
}

interface ComputerSkill {
  id: string;
  name: string;
  level: number;
  category: 'office' | 'accounting' | 'programming' | 'os' | 'database' | 'design';
}

interface ComputerSkills {
  office: ComputerSkill[];
  accounting: ComputerSkill[];
  programming: ComputerSkill[];
  os: ComputerSkill[];
  database: ComputerSkill[];
  design: ComputerSkill[];
}

interface DrivingSkill {
  id: string;
  category: string;
  experience: string;
}

interface OrganizationMembership {
  id: string;
  name: string;
  status: string;
  period: string;
}

interface Recommendation {
  id: string;
  personName: string;
  company: string;
  position: string;
  phone: string;
  email: string;
}

interface JobPreferences {
  workLocation: string;
  workField: string;
  workType: string;
  workTime: string;
  position: string;
  salary: string;
}

interface CVData {
  title: string;
  personalInfo: {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    address: string;
    birthDate: string;
    description: string;
  };
  workExperience: WorkExperience[];
  education: Education[];
  additionalTraining: AdditionalTraining[];
  languageSkills: LanguageSkill[];
  computerSkills: ComputerSkills;
  drivingSkills: DrivingSkill[];
  personalQualities: string;
  hobbies: string;
  organizationMemberships: OrganizationMembership[];
  recommendations: Recommendation[];
  jobPreferences: JobPreferences;
  cvVisibilityRestriction: boolean;
}

const CVEditor: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { id: cvId } = useParams<{ id: string }>();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [profileImage, setProfileImage] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [cvTemplates, setCvTemplates] = useState<{ [category: string]: any[] }>({});
  
  const [cvData, setCvData] = useState<CVData>({
    title: 'Mano CV',
    personalInfo: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      address: '',
      birthDate: '',
      description: ''
    },
    workExperience: [],
    education: [],
    additionalTraining: [],
    languageSkills: [],
    computerSkills: {
      office: [],
      accounting: [],
      programming: [],
      os: [],
      database: [],
      design: []
    },
    drivingSkills: [],
    personalQualities: '',
    hobbies: '',
    organizationMemberships: [],
    recommendations: [],
    jobPreferences: {
      workLocation: '',
      workField: '',
      workType: '',
      workTime: '',
      position: '',
      salary: ''
    },
    cvVisibilityRestriction: false
  });

  const [profileImageFile, setProfileImageFile] = useState<File | null>(null);
  
  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Store the file for upload
      setProfileImageFile(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setProfileImage(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  // Work Experience functions
  const addWorkExperience = () => {
    const newExp: WorkExperience = {
      id: Date.now().toString(),
      period: '',
      startDate: '',
      endDate: '',
      duration: '',
      companyName: '',
      companyWebsite: '',
      position: '',
      workNature: '',
      achievements: ''
    };
    setCvData(prev => ({
      ...prev,
      workExperience: [...prev.workExperience, newExp]
    }));
  };

  const removeWorkExperience = (id: string) => {
    setCvData(prev => ({
      ...prev,
      workExperience: prev.workExperience.filter(exp => exp.id !== id)
    }));
  };

  const calculateDuration = (startDate: string, endDate: string): string => {
    if (!startDate || !endDate) return '';
    
    const start = new Date(startDate);
    const end = new Date(endDate);
    
    if (start > end) return '';
    
    const diffTime = Math.abs(end.getTime() - start.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    const years = Math.floor(diffDays / 365);
    const months = Math.floor((diffDays % 365) / 30);
    
    let duration = '';
    if (years > 0) {
      const yearWord = years === 1 ? 'metai' : years < 10 ? 'metai' : 'metų';
      duration += `${years} ${yearWord}`;
    }
    if (months > 0) {
      if (duration) duration += ' ';
      const monthWord = months === 1 ? 'mėnuo' : months < 10 ? 'mėnesiai' : 'mėnesių';
      duration += `${months} ${monthWord}`;
    }
    
    return duration || '0 mėnesiai';
  };

  const updateWorkExperience = (id: string, field: keyof WorkExperience, value: string) => {
    setCvData(prev => ({
      ...prev,
      workExperience: prev.workExperience.map(exp => {
        if (exp.id === id) {
          const updatedExp = { ...exp, [field]: value };
          
          // Auto-calculate duration when dates change
          if (field === 'startDate' || field === 'endDate') {
            const startDate = field === 'startDate' ? value : exp.startDate;
            const endDate = field === 'endDate' ? value : exp.endDate;
            updatedExp.duration = calculateDuration(startDate, endDate);
            
            // Also update the period field for display
            if (startDate && endDate) {
              const start = new Date(startDate);
              const end = new Date(endDate);
              updatedExp.period = `${start.getFullYear()}.${String(start.getMonth() + 1).padStart(2, '0')} - ${end.getFullYear()}.${String(end.getMonth() + 1).padStart(2, '0')}`;
            }
          }
          
          return updatedExp;
        }
        return exp;
      })
    }));
  };

  // Education functions
  const addEducation = () => {
    const newEdu: Education = {
      id: Date.now().toString(),
      startDate: '',
      endDate: '',
      studyPeriod: '',
      institutionName: '',
      educationLevel: '',
      fieldOfStudy: ''
    };
    setCvData(prev => ({
      ...prev,
      education: [...prev.education, newEdu]
    }));
  };

  const removeEducation = (id: string) => {
    setCvData(prev => ({
      ...prev,
      education: prev.education.filter(edu => edu.id !== id)
    }));
  };

  const updateEducation = (id: string, field: keyof Education, value: string) => {
    setCvData(prev => ({
      ...prev,
      education: prev.education.map(edu => {
        if (edu.id === id) {
          const updatedEdu = { ...edu, [field]: value };
          
          // Auto-calculate study period when dates change
          if (field === 'startDate' || field === 'endDate') {
            const startDate = field === 'startDate' ? value : edu.startDate;
            const endDate = field === 'endDate' ? value : edu.endDate;
            
            if (startDate && endDate) {
              const start = new Date(startDate);
              const end = new Date(endDate);
              updatedEdu.studyPeriod = `${start.getFullYear()} - ${end.getFullYear()}`;
            }
          }
          
          return updatedEdu;
        }
        return edu;
      })
    }));
  };

  // Additional Training functions
  const addAdditionalTraining = () => {
    const newTraining: AdditionalTraining = {
      id: Date.now().toString(),
      year: '',
      duration: '',
      provider: '',
      courseName: ''
    };
    setCvData(prev => ({
      ...prev,
      additionalTraining: [...prev.additionalTraining, newTraining]
    }));
  };

  const removeAdditionalTraining = (id: string) => {
    setCvData(prev => ({
      ...prev,
      additionalTraining: prev.additionalTraining.filter(training => training.id !== id)
    }));
  };

  const updateAdditionalTraining = (id: string, field: keyof AdditionalTraining, value: string) => {
    setCvData(prev => ({
      ...prev,
      additionalTraining: prev.additionalTraining.map(training =>
        training.id === id ? { ...training, [field]: value } : training
      )
    }));
  };

  // Language Skills functions
  const addLanguageSkill = () => {
    const newLang: LanguageSkill = {
      id: Date.now().toString(),
      language: '',
      understanding: 1,
      speaking: 1,
      writing: 1
    };
    setCvData(prev => ({
      ...prev,
      languageSkills: [...prev.languageSkills, newLang]
    }));
  };

  const removeLanguageSkill = (id: string) => {
    setCvData(prev => ({
      ...prev,
      languageSkills: prev.languageSkills.filter(lang => lang.id !== id)
    }));
  };

  const updateLanguageSkill = (id: string, field: keyof LanguageSkill, value: string | number) => {
    setCvData(prev => ({
      ...prev,
      languageSkills: prev.languageSkills.map(lang =>
        lang.id === id ? { ...lang, [field]: value } : lang
      )
    }));
  };

  // Driving Skills functions
  const addDrivingSkill = () => {
    const newDriving: DrivingSkill = {
      id: Date.now().toString(),
      category: '',
      experience: ''
    };
    setCvData(prev => ({
      ...prev,
      drivingSkills: [...prev.drivingSkills, newDriving]
    }));
  };

  const removeDrivingSkill = (id: string) => {
    setCvData(prev => ({
      ...prev,
      drivingSkills: prev.drivingSkills.filter(skill => skill.id !== id)
    }));
  };

  const updateDrivingSkill = (id: string, field: keyof DrivingSkill, value: string) => {
    setCvData(prev => ({
      ...prev,
      drivingSkills: prev.drivingSkills.map(skill =>
        skill.id === id ? { ...skill, [field]: value } : skill
      )
    }));
  };

  // Organization Membership functions
  const addOrganizationMembership = () => {
    const newMembership: OrganizationMembership = {
      id: Date.now().toString(),
      name: '',
      status: '',
      period: ''
    };
    setCvData(prev => ({
      ...prev,
      organizationMemberships: [...prev.organizationMemberships, newMembership]
    }));
  };

  const removeOrganizationMembership = (id: string) => {
    setCvData(prev => ({
      ...prev,
      organizationMemberships: prev.organizationMemberships.filter(membership => membership.id !== id)
    }));
  };

  const updateOrganizationMembership = (id: string, field: keyof OrganizationMembership, value: string) => {
    setCvData(prev => ({
      ...prev,
      organizationMemberships: prev.organizationMemberships.map(membership =>
        membership.id === id ? { ...membership, [field]: value } : membership
      )
    }));
  };

  // Recommendation functions
  const addRecommendation = () => {
    const newRecommendation: Recommendation = {
      id: Date.now().toString(),
      personName: '',
      company: '',
      position: '',
      phone: '',
      email: ''
    };
    setCvData(prev => ({
      ...prev,
      recommendations: [...prev.recommendations, newRecommendation]
    }));
  };

  const removeRecommendation = (id: string) => {
    setCvData(prev => ({
      ...prev,
      recommendations: prev.recommendations.filter(rec => rec.id !== id)
    }));
  };

  // Computer skills helper functions
  const addComputerSkill = (category: keyof ComputerSkills) => {
    const newSkill: ComputerSkill = {
      id: Date.now().toString(),
      name: '',
      level: 1,
      category
    };
    
    setCvData(prev => ({
      ...prev,
      computerSkills: {
        ...prev.computerSkills,
        [category]: [...prev.computerSkills[category], newSkill]
      }
    }));
  };

  const updateComputerSkill = (category: keyof ComputerSkills, id: string, field: keyof ComputerSkill, value: string | number) => {
    setCvData(prev => ({
      ...prev,
      computerSkills: {
        ...prev.computerSkills,
        [category]: prev.computerSkills[category].map(skill =>
          skill.id === id ? { ...skill, [field]: value } : skill
        )
      }
    }));
  };

  const removeComputerSkill = (category: keyof ComputerSkills, id: string) => {
    setCvData(prev => ({
      ...prev,
      computerSkills: {
        ...prev.computerSkills,
        [category]: prev.computerSkills[category].filter(skill => skill.id !== id)
      }
    }));
  };

  const renderComputerSkillCategory = (category: keyof ComputerSkills, title: string) => {
    const templateCategory = `computer_skill_${category}` as keyof typeof cvTemplates;
    const availableSkills = cvTemplates[templateCategory] || [];
    
    return (
      <div className="skill-category">
        <div className="category-header">
          <h4>{title}</h4>
          <button 
            onClick={() => addComputerSkill(category)} 
            className="add-skill-btn"
            type="button"
          >
            <Plus size={14} />
          </button>
        </div>
        
        {cvData.computerSkills[category].map((skill) => (
          <div key={skill.id} className="skill-item">
            <div className="skill-input-group">
              <select
                value={skill.name}
                onChange={(e) => updateComputerSkill(category, skill.id, 'name', e.target.value)}
                className="skill-name-select"
              >
                <option value="">Pasirinkite įgūdį</option>
                {availableSkills.map((template) => (
                  <option key={template.id} value={template.value}>
                    {template.value}
                  </option>
                ))}
              </select>
              <button 
                onClick={() => removeComputerSkill(category, skill.id)} 
                className="remove-skill-btn"
                type="button"
              >
                <Trash2 size={14} />
              </button>
            </div>
            {skill.name && (
              <div className="skill-level-container">
                <label>Lygis:</label>
                {renderSkillBar(skill.level, (value) => updateComputerSkill(category, skill.id, 'level', value))}
              </div>
            )}
          </div>
        ))}
        
        {cvData.computerSkills[category].length === 0 && (
          <div className="empty-category">
            <p>Pridėkite {title.toLowerCase()} įgūdžius</p>
          </div>
        )}
      </div>
    );
  };

  const updateRecommendation = (id: string, field: keyof Recommendation, value: string) => {
    setCvData(prev => ({
      ...prev,
      recommendations: prev.recommendations.map(rec =>
        rec.id === id ? { ...rec, [field]: value } : rec
      )
    }));
  };

  const renderSkillBar = (value: number, onChange: (value: number) => void) => {
    return (
      <div className="skill-bar">
        {[1, 2, 3, 4, 5].map(level => (
          <div
            key={level}
            className={`skill-level ${value >= level ? 'active' : ''}`}
            onClick={() => onChange(level)}
          />
        ))}
      </div>
    );
  };

  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState<string | null>(null);

  // Load CV templates
  const loadCVTemplates = async () => {
    try {
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
      const response = await fetch(`${API_URL}/cv-templates?grouped=true`);
      if (response.ok) {
        const result = await response.json();
        setCvTemplates(result.data);
      }
    } catch (error) {
      console.error('Error loading CV templates:', error);
    }
  };

  // Load existing CV data on component mount
  useEffect(() => {
    const loadExistingCV = async () => {
      if (!user) return;
      
      // If no cvId, we're creating a new CV - skip loading
      if (!cvId) {
        setIsLoading(false);
        return;
      }
      
      try {
        const token = localStorage.getItem('auth_token');
        const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
        const response = await fetch(`${API_URL}/cv/${cvId}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const result = await response.json();
          const existingCV = result.data;
          
          console.log('📥 Loaded CV data:', existingCV);
          console.log('📅 Birth date from backend:', existingCV.birth_date);
          
          // Map backend data to frontend structure
          setCvData({
            title: existingCV.title || 'Mano CV',
            personalInfo: {
              firstName: existingCV.first_name || '',
              lastName: existingCV.last_name || '',
              email: existingCV.email || user.email || '',
              phone: existingCV.phone || '',
              address: existingCV.address || '',
              birthDate: existingCV.birth_date || '',
              description: existingCV.description || ''
            },
            workExperience: (existingCV.work_experience || []).map((exp: any) => ({
              ...exp,
              startDate: exp.startDate || '',
              endDate: exp.endDate || '',
              duration: exp.duration || '',
              period: exp.period || ''
            })),
            education: existingCV.education || [],
            additionalTraining: existingCV.additional_training || [],
            languageSkills: existingCV.language_skills || [],
            computerSkills: existingCV.computer_skills || {
              office: [],
              accounting: [],
              programming: [],
              os: [],
              database: [],
              design: []
            },
            drivingSkills: existingCV.driving_skills || [],
            personalQualities: existingCV.personal_qualities || '',
            hobbies: existingCV.hobbies || '',
            organizationMemberships: existingCV.organization_memberships || [],
            recommendations: existingCV.recommendations || [],
            jobPreferences: existingCV.job_preferences || {
              workLocation: '',
              workField: '',
              workType: '',
              workTime: '',
              position: '',
              salary: ''
            },
            cvVisibilityRestriction: existingCV.cv_visibility_restriction || false
          });
          
          if (existingCV.profile_image_url) {
            // Backend now returns paths like /uploads/cvs/filename.jpg
            const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
            const baseUrl = API_URL.replace('/api', '');
            const imageUrl = existingCV.profile_image_url.startsWith('http') 
              ? existingCV.profile_image_url 
              : `${baseUrl}${existingCV.profile_image_url}`;
            setProfileImage(imageUrl);
          }
        }
      } catch (error) {
        console.error('Error loading CV:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadCVTemplates();
    loadExistingCV();
  }, [user, cvId]);

  const handleSave = async () => {
    if (!user) return;
    
    setIsSaving(true);
    setSaveMessage(null);
    
    try {
      const token = localStorage.getItem('auth_token');
      
      console.log('💾 Saving CV...', cvId ? `Updating CV ID: ${cvId}` : 'Creating new CV');
      console.log('📝 CV Data:', cvData);
      console.log('📅 Birth date being saved:', cvData.personalInfo.birthDate);
      
      // Map frontend data to backend structure
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
      
      // Use FormData if there's an image to upload
      let response;
      if (profileImageFile) {
        const formData = new FormData();
        formData.append('profile_image', profileImageFile);
        formData.append('title', cvData.title);
        formData.append('first_name', cvData.personalInfo.firstName);
        formData.append('last_name', cvData.personalInfo.lastName);
        formData.append('email', cvData.personalInfo.email);
        formData.append('phone', cvData.personalInfo.phone);
        formData.append('address', cvData.personalInfo.address);
        formData.append('birth_date', cvData.personalInfo.birthDate);
        formData.append('description', cvData.personalInfo.description);
        formData.append('work_experience', JSON.stringify(cvData.workExperience));
        formData.append('education', JSON.stringify(cvData.education));
        formData.append('additional_training', JSON.stringify(cvData.additionalTraining));
        const validLanguageSkills = cvData.languageSkills.filter(
          lang => lang.language.trim() !== '' && (lang.understanding > 0 || lang.speaking > 0 || lang.writing > 0)
        );
        formData.append('language_skills', JSON.stringify(validLanguageSkills));
        formData.append('computer_skills', JSON.stringify(cvData.computerSkills));
        formData.append('driving_skills', JSON.stringify(cvData.drivingSkills));
        formData.append('personal_qualities', JSON.stringify(cvData.personalQualities));
        formData.append('hobbies', JSON.stringify(cvData.hobbies));
        formData.append('organization_memberships', JSON.stringify(cvData.organizationMemberships));
        formData.append('recommendations', JSON.stringify(cvData.recommendations));
        formData.append('job_preferences', JSON.stringify(cvData.jobPreferences));
        formData.append('cv_visibility_restriction', String(cvData.cvVisibilityRestriction));

        // Decide endpoint and method based on whether we're creating or updating
        const endpoint = cvId ? `${API_URL}/cv/${cvId}` : `${API_URL}/cv`;
        const method = cvId ? 'PUT' : 'POST';
        
        response = await fetch(endpoint, {
          method: method,
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: formData
        });
      } else {
        // No new image, send JSON
        const backendData = {
          title: cvData.title,
          first_name: cvData.personalInfo.firstName,
          last_name: cvData.personalInfo.lastName,
          email: cvData.personalInfo.email,
          phone: cvData.personalInfo.phone,
          address: cvData.personalInfo.address,
          birth_date: cvData.personalInfo.birthDate,
          description: cvData.personalInfo.description,
          profile_image_url: profileImage && !profileImage.startsWith('data:') ? profileImage : null,
          work_experience: cvData.workExperience,
          education: cvData.education,
          additional_training: cvData.additionalTraining,
          language_skills: cvData.languageSkills.filter(
            lang => lang.language.trim() !== '' && (lang.understanding > 0 || lang.speaking > 0 || lang.writing > 0)
          ),
          computer_skills: cvData.computerSkills,
          driving_skills: cvData.drivingSkills,
          personal_qualities: cvData.personalQualities,
          hobbies: cvData.hobbies,
          organization_memberships: cvData.organizationMemberships,
          recommendations: cvData.recommendations,
          job_preferences: cvData.jobPreferences,
          cv_visibility_restriction: cvData.cvVisibilityRestriction
        };

        // Decide endpoint and method based on whether we're creating or updating
        const endpoint = cvId ? `${API_URL}/cv/${cvId}` : `${API_URL}/cv`;
        const method = cvId ? 'PUT' : 'POST';
        
        response = await fetch(endpoint, {
          method: method,
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(backendData)
        });
      }

      if (response.ok) {
        const result = await response.json();
        console.log('✅ CV saved successfully:', result);
        setSaveMessage(cvId ? 'CV sėkmingai atnaujintas! ✅' : 'CV sėkmingai sukurtas! ✅');
        
        // Redirect to Mano CV page after 1.5 seconds
        setTimeout(() => {
          navigate('/mano-cv');
        }, 1500);
      } else {
        const errorData = await response.json();
        console.error('❌ Failed to save CV:', errorData);
        throw new Error(errorData.message || 'Failed to save CV');
      }
      
    } catch (error) {
      console.error('Error saving CV:', error);
      setSaveMessage('Klaida išsaugant CV. Bandykite dar kartą. ❌');
      
      // Clear error message after 5 seconds
      setTimeout(() => {
        setSaveMessage(null);
      }, 5000);
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="cv-editor">
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Kraunami CV duomenys...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="cv-editor">
      <div className="cv-header">
        <h2>{cvId ? 'CV Redagavimas' : 'CV Kūrimas'}</h2>
        <div className="header-actions">
          {saveMessage && (
            <div className={`save-message ${saveMessage.includes('❌') ? 'error' : 'success'}`}>
              {saveMessage}
            </div>
          )}
          <button 
            className="save-btn" 
            onClick={handleSave}
            disabled={isSaving}
          >
            <Save size={16} />
            {isSaving ? 'Išsaugoma...' : 'Išsaugoti'}
          </button>
        </div>
      </div>

      {/* CV Title */}
      <div className="cv-section">
        <div className="form-group">
          <label>CV Pavadinimas *</label>
          <input
            type="text"
            className="form-input"
            value={cvData.title}
            onChange={(e) => setCvData({ ...cvData, title: e.target.value })}
            placeholder="pvz., Mano CV, Vadybininko CV..."
            required
          />
        </div>
      </div>

      {/* Header Section (Antraštės skyrius) */}
      <div className="cv-section">
        <h3>Asmeninė informacija</h3>
        <div className="profile-photo-section">
          <div className="photo-upload">
            {profileImage ? (
              <img src={profileImage} alt="Profile" className="profile-image" />
            ) : (
              <div className="photo-placeholder">
                <Camera size={24} />
                <span>Profilio nuotrauka</span>
              </div>
            )}
            <button onClick={() => fileInputRef.current?.click()} className="upload-btn">
              <Camera size={16} />
              Redagavimo mygtukas
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
              style={{ display: 'none' }}
            />
          </div>
        </div>

        <div className="form-grid">
          <div className="form-group">
            <label>Vardas</label>
            <input
              type="text"
              value={cvData.personalInfo.firstName}
              onChange={(e) => setCvData(prev => ({
                ...prev,
                personalInfo: { ...prev.personalInfo, firstName: e.target.value }
              }))}
              placeholder="Jonas"
            />
          </div>
          <div className="form-group">
            <label>Pavardė</label>
            <input
              type="text"
              value={cvData.personalInfo.lastName}
              onChange={(e) => setCvData(prev => ({
                ...prev,
                personalInfo: { ...prev.personalInfo, lastName: e.target.value }
              }))}
              placeholder="Petraitis"
            />
          </div>
          <div className="form-group">
            <label>Gimimo data</label>
            <DatePicker
              value={cvData.personalInfo.birthDate}
              onChange={(val) => setCvData(prev => ({
                ...prev,
                personalInfo: { ...prev.personalInfo, birthDate: val }
              }))}
              maxDate={new Date().toISOString().split('T')[0]}
            />
          </div>
          <AutocompleteInput
            label="Gyvenamoji vieta"
            value={cvData.personalInfo.address}
            onChange={(value) => setCvData(prev => ({
              ...prev,
              personalInfo: { ...prev.personalInfo, address: value }
            }))}
            options={cvTemplates.city?.map(city => ({ id: city.id, value: city.value })) || []}
            placeholder="Pradėkite rašyti miesto pavadinimą..."
            allowCustomValue={true}
          />
          <div className="form-group">
            <label>Telefono numeris</label>
            <input
              type="tel"
              value={cvData.personalInfo.phone}
              onChange={(e) => setCvData(prev => ({
                ...prev,
                personalInfo: { ...prev.personalInfo, phone: e.target.value }
              }))}
              placeholder="+370"
            />
          </div>
          <div className="form-group">
            <label>El. paštas</label>
            <input
              type="email"
              value={cvData.personalInfo.email}
              onChange={(e) => setCvData(prev => ({
                ...prev,
                personalInfo: { ...prev.personalInfo, email: e.target.value }
              }))}
              placeholder="petraitis@gmail.lt"
            />
          </div>
        </div>

        <div className="form-group full-width">
          <label>Profesinis aprašymas</label>
          <textarea
            value={cvData.personalInfo.description}
            onChange={(e) => setCvData(prev => ({
              ...prev,
              personalInfo: { ...prev.personalInfo, description: e.target.value }
            }))}
            rows={4}
            placeholder="Esu kvalifikuotas ir patyręs personalo vadybos, turiu daugiau metų aktyvų pardavimų, klientų aptarnavimo darbu tarptautinėse kompanijose, pageidaujamu, aktyvus žmogus"
          />
        </div>
      </div>

      {/* Darbo patirtis */}
      <div className="cv-section">
        <div className="section-header">
          <h3>Darbo patirtis</h3>
          <button onClick={addWorkExperience} className="add-btn">
            <Plus size={16} />
            Pridėti kitą darbovetę
          </button>
        </div>

        {cvData.workExperience.map((exp) => (
          <div key={exp.id} className="experience-item">
            <div className="item-header">
              <h4>Darbo patirtis</h4>
              <button onClick={() => removeWorkExperience(exp.id)} className="remove-btn">
                <Trash2 size={16} />
              </button>
            </div>
            
            <div className="form-grid">
              <div className="form-group">
                <label>Darbo pradžia (nuo)</label>
                <DatePicker
                  value={exp.startDate}
                  onChange={(val) => updateWorkExperience(exp.id, 'startDate', val)}
                  maxDate={new Date().toISOString().split('T')[0]}
                />
              </div>
              <div className="form-group">
                <label>Darbo pabaiga (iki)</label>
                <DatePicker
                  value={exp.endDate}
                  onChange={(val) => updateWorkExperience(exp.id, 'endDate', val)}
                  minDate={exp.startDate || undefined}
                  maxDate={new Date().toISOString().split('T')[0]}
                />
              </div>
              <div className="form-group">
                <label>Laikotarpis</label>
                <input
                  type="text"
                  value={exp.period}
                  readOnly
                  placeholder="Automatiškai apskaičiuojama"
                  className="readonly-field"
                />
              </div>
              <div className="form-group">
                <label>Trukmė</label>
                <input
                  type="text"
                  value={exp.duration}
                  readOnly
                  placeholder="Automatiškai apskaičiuojama"
                  className="readonly-field"
                />
              </div>
              <div className="form-group">
                <label>Įmonės pavadinimas</label>
                <input
                  type="text"
                  value={exp.companyName}
                  onChange={(e) => updateWorkExperience(exp.id, 'companyName', e.target.value)}
                  placeholder='UAB "Geras"'
                />
              </div>
              <div className="form-group">
                <label>Įmonės svetainė</label>
                <input
                  type="url"
                  value={exp.companyWebsite}
                  onChange={(e) => updateWorkExperience(exp.id, 'companyWebsite', e.target.value)}
                  placeholder="https://www.example.com"
                />
              </div>
              <AutocompleteInput
                label="Pareigos"
                value={exp.position}
                onChange={(value) => updateWorkExperience(exp.id, 'position', value)}
                options={cvTemplates.position?.map(pos => ({ id: pos.id, value: pos.value })) || []}
                placeholder="Pradėkite rašyti pareigas..."
                allowCustomValue={true}
              />
            </div>
            
            <div className="form-group full-width">
              <label>Darbo pobūdis</label>
              <textarea
                value={exp.workNature}
                onChange={(e) => updateWorkExperience(exp.id, 'workNature', e.target.value)}
                rows={3}
                placeholder="• Pardavimų organizavimas klientams
• Naujų klientų paieška
• Prekybos salės priežiūra
• Atsiskaitymų kontrolė"
              />
            </div>

            <div className="form-group full-width">
              <label>Pasiekimai</label>
              <textarea
                value={exp.achievements}
                onChange={(e) => updateWorkExperience(exp.id, 'achievements', e.target.value)}
                rows={3}
                placeholder="• Įmonės apyvartos padidėjo 30%
• Klientų skaičius - 15%
• Pagrinė pardavimų vadybė"
              />
            </div>
          </div>
        ))}
      </div>

      {/* Išsilavinimas */}
      <div className="cv-section">
        <div className="section-header">
          <h3>Išsilavinimas</h3>
          <button onClick={addEducation} className="add-btn">
            <Plus size={16} />
            Pridėti kitą mokymo įstaigą
          </button>
        </div>

        {cvData.education.map((edu) => (
          <div key={edu.id} className="experience-item">
            <div className="item-header">
              <h4>Išsilavinimas</h4>
              <button onClick={() => removeEducation(edu.id)} className="remove-btn">
                <Trash2 size={16} />
              </button>
            </div>
            
            <div className="form-grid">
              <div className="form-group">
                <label>Studijų pradžia</label>
                <DatePicker
                  value={edu.startDate}
                  onChange={(val) => updateEducation(edu.id, 'startDate', val)}
                  maxDate={new Date().toISOString().split('T')[0]}
                />
              </div>
              <div className="form-group">
                <label>Studijų pabaiga</label>
                <DatePicker
                  value={edu.endDate}
                  onChange={(val) => updateEducation(edu.id, 'endDate', val)}
                  minDate={edu.startDate || undefined}
                />
              </div>
              <div className="form-group">
                <label>Studijų laikotarpis</label>
                <input
                  type="text"
                  value={edu.studyPeriod}
                  readOnly
                  placeholder="Automatiškai apskaičiuojama"
                  className="readonly-field"
                />
              </div>
              <AutocompleteInput
                label="Mokymo įstaigos pavadinimas"
                value={edu.institutionName}
                onChange={(value) => updateEducation(edu.id, 'institutionName', value)}
                options={cvTemplates.institution?.map(inst => ({ id: inst.id, value: inst.value })) || []}
                placeholder="Pradėkite rašyti įstaigos pavadinimą..."
                allowCustomValue={true}
              />
              <div className="form-group">
                <label>Išsilavinimo lygis</label>
                <select
                  value={edu.educationLevel}
                  onChange={(e) => updateEducation(edu.id, 'educationLevel', e.target.value)}
                >
                  <option value="">Pasirinkite išsilavinimo lygį</option>
                  {cvTemplates.education_level?.map((template) => (
                    <option key={template.id} value={template.value}>
                      {template.value}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Studijų sritis</label>
                <select
                  value={edu.fieldOfStudy}
                  onChange={(e) => updateEducation(edu.id, 'fieldOfStudy', e.target.value)}
                >
                  <option value="">Pasirinkite studijų sritį</option>
                  {cvTemplates.field_of_study?.map((template) => (
                    <option key={template.id} value={template.value}>
                      {template.value}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Papildomi mokymai, seminarai, kursai */}
      <div className="cv-section">
        <div className="section-header">
          <h3>Papildomi mokymai, seminarai, kursai</h3>
          <button onClick={addAdditionalTraining} className="add-btn">
            <Plus size={16} />
            Pridėti kitus mokymus
          </button>
        </div>

        {cvData.additionalTraining.map((training) => (
          <div key={training.id} className="experience-item">
            <div className="item-header">
              <h4>Papildomi mokymai</h4>
              <button onClick={() => removeAdditionalTraining(training.id)} className="remove-btn">
                <Trash2 size={16} />
              </button>
            </div>
            
            <div className="form-grid">
              <div className="form-group">
                <label>Metai</label>
                <input
                  type="text"
                  value={training.year}
                  onChange={(e) => updateAdditionalTraining(training.id, 'year', e.target.value)}
                  placeholder="2011"
                />
              </div>
              <div className="form-group">
                <label>Trukmė</label>
                <input
                  type="text"
                  value={training.duration}
                  onChange={(e) => updateAdditionalTraining(training.id, 'duration', e.target.value)}
                  placeholder="40 val."
                />
              </div>
              <div className="form-group">
                <label>Mokymo teikėjas</label>
                <input
                  type="text"
                  value={training.provider}
                  onChange={(e) => updateAdditionalTraining(training.id, 'provider', e.target.value)}
                  placeholder="Nepriklausomy Verslo Konsultantų Grupė"
                />
              </div>
              <div className="form-group">
                <label>Kurso pavadinimas</label>
                <input
                  type="text"
                  value={training.courseName}
                  onChange={(e) => updateAdditionalTraining(training.id, 'courseName', e.target.value)}
                  placeholder='"Viso organizacijos personalo jungimas"'
                />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Kalbų įgūdžiai */}
      <div className="cv-section">
        <div className="section-header">
          <h3>Kalbų įgūdžiai</h3>
          <button onClick={addLanguageSkill} className="add-btn">
            <Plus size={16} />
            Pridėti kitą kalbą
          </button>
        </div>

        {cvData.languageSkills.map((lang) => (
          <div key={lang.id} className="language-item">
            <div className="item-header">
              <select
                value={lang.language}
                onChange={(e) => updateLanguageSkill(lang.id, 'language', e.target.value)}
                className="language-name-select"
              >
                <option value="">Pasirinkite kalbą</option>
                {cvTemplates.language?.map((template) => (
                  <option key={template.id} value={template.value}>
                    {template.value}
                  </option>
                ))}
              </select>
              <button onClick={() => removeLanguageSkill(lang.id)} className="remove-btn">
                <Trash2 size={16} />
              </button>
            </div>
            
            <div className="language-skills-grid">
              <div className="skill-group">
                <label>Supratimas</label>
                {renderSkillBar(lang.understanding, (value) => updateLanguageSkill(lang.id, 'understanding', value))}
              </div>
              <div className="skill-group">
                <label>Kalbėjimas</label>
                {renderSkillBar(lang.speaking, (value) => updateLanguageSkill(lang.id, 'speaking', value))}
              </div>
              <div className="skill-group">
                <label>Rašymas</label>
                {renderSkillBar(lang.writing, (value) => updateLanguageSkill(lang.id, 'writing', value))}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Darbo kompiuterių įgūdžiai */}
      <div className="cv-section">
        <h3>Darbo kompiuterių įgūdžiai</h3>
        
        <div className="computer-skills-grid">
          {renderComputerSkillCategory('office', 'Biuro programos')}
          {renderComputerSkillCategory('accounting', 'Apskaitos programos')}
          {renderComputerSkillCategory('programming', 'Programavimo kalbos')}
          {renderComputerSkillCategory('os', 'Operacinės sistemos')}
          {renderComputerSkillCategory('database', 'Duomenų bazės')}
          {renderComputerSkillCategory('design', 'Dizaino programos')}
        </div>
      </div>

      {/* Vairavimo įgūdžiai */}
      <div className="cv-section">
        <div className="section-header">
          <h3>Vairavimo įgūdžiai</h3>
          <button onClick={addDrivingSkill} className="add-btn">
            <Plus size={16} />
            Pridėti kitą kategoriją
          </button>
        </div>

        {cvData.drivingSkills.map((skill) => (
          <div key={skill.id} className="driving-item">
            <div className="form-grid">
              <div className="form-group">
                <label>Kategorija</label>
                <select
                  value={skill.category}
                  onChange={(e) => updateDrivingSkill(skill.id, 'category', e.target.value)}
                >
                  <option value="">Pasirinkite kategoriją</option>
                  <option value="B kategorija">B kategorija</option>
                  <option value="C kategorija">C kategorija</option>
                  <option value="D kategorija">D kategorija</option>
                  <option value="BE kategorija">BE kategorija</option>
                  <option value="CE kategorija">CE kategorija</option>
                </select>
              </div>
              <div className="form-group">
                <label>Patirtis</label>
                <input
                  type="text"
                  value={skill.experience}
                  onChange={(e) => updateDrivingSkill(skill.id, 'experience', e.target.value)}
                  placeholder="Patirtis 8 metai"
                />
              </div>
              <div className="form-group">
                <button onClick={() => removeDrivingSkill(skill.id)} className="remove-btn">
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Asmeninės savybės */}
      <div className="cv-section">
        <h3>Asmeninės savybės</h3>
        <div className="form-group full-width">
          <textarea
            value={cvData.personalQualities}
            onChange={(e) => setCvData(prev => ({
              ...prev,
              personalQualities: e.target.value
            }))}
            rows={4}
            placeholder="Esu komunikabilius, atsakingas, darštus, siekiantis tikslų, orientuotas į geramus rezultatus. Mėgstu dirbti, naujovas, greitai prisitaikau prie naujų darbuotojų aplinkų."
          />
        </div>
      </div>

      {/* Pomėgiai, hobiai */}
      <div className="cv-section">
        <h3>Pomėgiai, hobiai</h3>
        <div className="form-group full-width">
          <textarea
            value={cvData.hobbies}
            onChange={(e) => setCvData(prev => ({
              ...prev,
              hobbies: e.target.value
            }))}
            rows={3}
            placeholder="Sportas, knygos, muzika, filmai, teatras, aktyvūs laisvalaikiai, žvejyba"
          />
        </div>
      </div>

      {/* Narystė organizacijose */}
      <div className="cv-section">
        <div className="section-header">
          <h3>Narystė organizacijose</h3>
          <button onClick={addOrganizationMembership} className="add-btn">
            <Plus size={16} />
            Pridėti kitą narystę
          </button>
        </div>

        {cvData.organizationMemberships.map((membership) => (
          <div key={membership.id} className="experience-item">
            <div className="item-header">
              <h4>Narystė organizacijoje</h4>
              <button onClick={() => removeOrganizationMembership(membership.id)} className="remove-btn">
                <Trash2 size={16} />
              </button>
            </div>
            
            <div className="form-grid">
              <div className="form-group">
                <label>Pavadinimas</label>
                <input
                  type="text"
                  value={membership.name}
                  onChange={(e) => updateOrganizationMembership(membership.id, 'name', e.target.value)}
                  placeholder="Lietuvos socialdemokratų partija"
                />
              </div>
              <div className="form-group">
                <label>Statusas</label>
                <input
                  type="text"
                  value={membership.status}
                  onChange={(e) => updateOrganizationMembership(membership.id, 'status', e.target.value)}
                  placeholder="Narys"
                />
              </div>
              <div className="form-group">
                <label>Laikotarpis</label>
                <input
                  type="text"
                  value={membership.period}
                  onChange={(e) => updateOrganizationMembership(membership.id, 'period', e.target.value)}
                  placeholder="2009 - dabar"
                />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Rekomendacijos */}
      <div className="cv-section">
        <div className="section-header">
          <h3>Rekomendacijos</h3>
          <button onClick={addRecommendation} className="add-btn">
            <Plus size={16} />
            Pridėti kitą rekomendaciją
          </button>
        </div>

        {cvData.recommendations.map((rec) => (
          <div key={rec.id} className="experience-item">
            <div className="item-header">
              <h4>Rekomendacija</h4>
              <button onClick={() => removeRecommendation(rec.id)} className="remove-btn">
                <Trash2 size={16} />
              </button>
            </div>
            
            <div className="form-grid">
              <div className="form-group">
                <label>Asmens vardas</label>
                <input
                  type="text"
                  value={rec.personName}
                  onChange={(e) => updateRecommendation(rec.id, 'personName', e.target.value)}
                  placeholder="Jonas Brokas"
                />
              </div>
              <div className="form-group">
                <label>Įmonė</label>
                <input
                  type="text"
                  value={rec.company}
                  onChange={(e) => updateRecommendation(rec.id, 'company', e.target.value)}
                  placeholder='UAB "Geras"'
                />
              </div>
              <div className="form-group">
                <label>Pareigos</label>
                <input
                  type="text"
                  value={rec.position}
                  onChange={(e) => updateRecommendation(rec.id, 'position', e.target.value)}
                  placeholder="Direktorius"
                />
              </div>
              <div className="form-group">
                <label>Telefonas</label>
                <input
                  type="tel"
                  value={rec.phone}
                  onChange={(e) => updateRecommendation(rec.id, 'phone', e.target.value)}
                  placeholder="+370"
                />
              </div>
              <div className="form-group">
                <label>El. paštas</label>
                <input
                  type="email"
                  value={rec.email}
                  onChange={(e) => updateRecommendation(rec.id, 'email', e.target.value)}
                  placeholder="jonas.brokas@geras.lt"
                />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Pageidaujamas darbas */}
      <div className="cv-section">
        <h3>Pageidaujamas darbas</h3>
        
        <div className="form-grid">
          <div className="form-group">
            <label>Darbo vieta</label>
            <select
              value={cvData.jobPreferences.workLocation}
              onChange={(e) => setCvData(prev => ({
                ...prev,
                jobPreferences: { ...prev.jobPreferences, workLocation: e.target.value }
              }))}
            >
              <option value="">Pasirinkite darbo vietą</option>
              {cvTemplates.work_location?.map((template) => (
                <option key={template.id} value={template.value}>
                  {template.value}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>Darbo sritis</label>
            <select
              value={cvData.jobPreferences.workField}
              onChange={(e) => setCvData(prev => ({
                ...prev,
                jobPreferences: { ...prev.jobPreferences, workField: e.target.value }
              }))}
            >
              <option value="">Pasirinkite darbo sritį</option>
              {cvTemplates.work_field?.map((template) => (
                <option key={template.id} value={template.value}>
                  {template.value}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>Darbo tipas</label>
            <select
              value={cvData.jobPreferences.workType}
              onChange={(e) => setCvData(prev => ({
                ...prev,
                jobPreferences: { ...prev.jobPreferences, workType: e.target.value }
              }))}
            >
              <option value="">Pasirinkite darbo tipą</option>
              {cvTemplates.work_type?.map((template) => (
                <option key={template.id} value={template.value}>
                  {template.value}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>Darbo laikas</label>
            <select
              value={cvData.jobPreferences.workTime}
              onChange={(e) => setCvData(prev => ({
                ...prev,
                jobPreferences: { ...prev.jobPreferences, workTime: e.target.value }
              }))}
            >
              <option value="">Pasirinkite darbo laiką</option>
              {cvTemplates.work_time?.map((template) => (
                <option key={template.id} value={template.value}>
                  {template.value}
                </option>
              ))}
            </select>
          </div>
          <AutocompleteInput
            label="Pareigos"
            value={cvData.jobPreferences.position}
            onChange={(value) => setCvData(prev => ({
              ...prev,
              jobPreferences: { ...prev.jobPreferences, position: value }
            }))}
            options={cvTemplates.job_preference_position?.map(pos => ({ id: pos.id, value: pos.value })) || []}
            placeholder="Pradėkite rašyti norimas pareigas..."
            allowCustomValue={true}
          />
          <div className="form-group">
            <label>Atlyginimas (netto)</label>
            <div className="salary-input-group">
              <input
                type="number"
                min="0"
                step="50"
                value={cvData.jobPreferences.salary.replace(' EUR', '')}
                onChange={(e) => {
                  const numericValue = e.target.value;
                  const salaryWithEUR = numericValue ? `${numericValue} EUR` : '';
                  setCvData(prev => ({
                    ...prev,
                    jobPreferences: { ...prev.jobPreferences, salary: salaryWithEUR }
                  }));
                }}
                placeholder="1500"
                className="salary-number-input"
              />
              <span className="salary-currency">EUR</span>
            </div>
          </div>
        </div>
      </div>

      {/* CV matomumo ribojimas įmonėms */}
      <div className="cv-section">
        <h3>CV matomumo ribojimas įmonėms</h3>
        <div className="form-group">
          <label className="visibility-toggle-label">
            CV nerodomas įmonėms
          </label>
          <div className="toggle-switch">
            <button
              type="button"
              className={`toggle-option ${!cvData.cvVisibilityRestriction ? 'active' : ''}`}
              onClick={() => setCvData(prev => ({
                ...prev,
                cvVisibilityRestriction: false
              }))}
            >
              Taip
            </button>
            <button
              type="button"
              className={`toggle-option ${cvData.cvVisibilityRestriction ? 'active' : ''}`}
              onClick={() => setCvData(prev => ({
                ...prev,
                cvVisibilityRestriction: true
              }))}
            >
              Ne
            </button>
          </div>
          <p className="visibility-description">
            {!cvData.cvVisibilityRestriction 
              ? "Jūsų CV bus matomas darbdaviams paieškos rezultatuose" 
              : "Jūsų CV nebus rodomas darbdaviams paieškos rezultatuose"
            }
          </p>
        </div>
      </div>
    </div>
  );
};

export default CVEditor;